import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Pencil, RefreshCw, AlertCircle, FileText } from 'lucide-react';
import { V2PageShell, Card, Button, Pill, EmptyState, Toast, Input } from '../../components/v2';
import { listForms } from '../../services/api';
import useFormsTabs from '../../hooks/useFormsTabs';
import useActiveScope from '../../hooks/useActiveScope';

/**
 * FormsDesignerIndexPage — RC-162c.
 *
 * <p>/forms/designer — author-first picker that lists every form_task /
 * human_task node across visible workflows and routes the operator
 * directly into the schema editor for the chosen one. Promotes the
 * Form Designer to a first-class top-level destination (instead of
 * being reachable only from a canvas Inspector deep-link).</p>
 *
 * <p><b>Author posture vs Library</b>. The Library page (RC-160 →
 * RC-162d) is read-focused — operators landing there scan in-flight
 * counts, mode pills, and assignment summaries. This page is
 * authoring-focused — every row's primary CTA is "Open Designer".
 * Runtime columns (in-flight count) are intentionally absent so the
 * author isn't distracted by execution metrics while choosing what
 * to edit. Same backing data source ({@code GET /api/v1/forms},
 * added in RC-163a) — different presentation.</p>
 *
 * <p><b>RBAC</b>. Route guard at the router level
 * ({@code workflow.read / Developer minRole} — see App.jsx),
 * mirrors the canvas RoleGuard. Hidden from the left nav for
 * Compliance / Auditor / Viewer per RC-162a.</p>
 *
 * <p>Built on V2 primitives + the shared FormsSubNav tab strip
 * (via useFormsTabs). Zero inline style; zero raw HTML inputs.</p>
 */
export default function FormsDesignerIndexPage() {
  const { tabs, activeTabId, onTabChange } = useFormsTabs();
  // RC-163a-scope-fix + RC-Instance-Scope-Enforcement (#210) — chip
  // narrowing mirrors /api/v1/workflows scope semantics across all
  // three axes (org / tenant / instance). Without these the catalogue
  // would return the caller's full envelope (SA: all tenants in
  // customer; OA: all tenants in org; IA: all instances under their
  // tenant) regardless of the chip — silent cross-tenant / cross-
  // instance leak. The hook returns null for any unset chip dimension;
  // the api client omits null params so non-SA callers continue to get
  // JWT-backfilled scope server-side.
  const { orgId, tenantId, instanceId } = useActiveScope();

  const [entries, setEntries] = useState(null);   // null = not loaded
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter] = useState('');

  const load = useCallback(async () => {
    setRefreshing(true);
    setError(null);
    try {
      const res = await listForms({ orgId, tenantId, instanceId });
      const list = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
      setEntries(list);
    } catch (e) {
      setError(e?.message || 'Could not load forms.');
      setEntries([]);
    } finally {
      setRefreshing(false);
    }
  }, [orgId, tenantId, instanceId]);

  useEffect(() => { load(); }, [load]);

  // Client-side filter — case-insensitive substring across workflow name,
  // node label, node id. The BE catalogue endpoint isn't paged (V1) so
  // filtering on the client is correct for typical catalogue sizes
  // (<500 entries). Future RC can promote to a server-side search when
  // a tenant breaches that threshold.
  const filtered = useMemo(() => {
    if (!entries) return [];
    const q = filter.trim().toLowerCase();
    if (!q) return entries;
    return entries.filter((e) => {
      const hay = `${e.workflowName ?? ''} ${e.nodeLabel ?? ''} ${e.nodeId ?? ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [entries, filter]);

  const actions = (
    <Button variant="secondary" size="sm" iconLeft={<RefreshCw />}
            onClick={load} loading={refreshing}>
      Refresh
    </Button>
  );

  return (
    <V2PageShell
      title="Forms · Designer"
      subtitle="Pick a form_task to edit its schema. Schema edits affect future assignments; in-flight assignments keep the snapshot they were created with."
      actions={actions}
      tabs={tabs}
      activeTabId={activeTabId}
      onTabChange={onTabChange}
    >
      {error && (
        <div className="mb-4">
          <Toast tone="danger" title="Could not load forms" description={error}
                 duration={0} onDismiss={() => setError(null)} />
        </div>
      )}

      {/* Filter strip — Input primitive only, no raw <input>.
          V2 Input does not currently expose an iconLeft slot (see
          components/v2/Input.jsx), so no decorative icon — the
          placeholder carries the affordance. */}
      <div className="mb-4 max-w-md">
        <Input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter by workflow, label, or node id…"
        />
      </div>

      {entries == null && (
        <Card><Card.Body>
          <div className="text-[13px] text-v2-ink-secondary py-6 text-center">Loading forms…</div>
        </Card.Body></Card>
      )}

      {entries != null && entries.length === 0 && (
        <EmptyAtChipScope
          orgId={orgId}
          tenantId={tenantId}
          instanceId={instanceId}
          surface="Designer"
          onShowUnfiltered={async () => {
            setRefreshing(true);
            setError(null);
            try {
              const res = await listForms({});  // no chip narrowing
              const list = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
              setEntries(list);
            } catch (e) {
              setError(e?.message || 'Could not load forms.');
            } finally {
              setRefreshing(false);
            }
          }}
        />
      )}

      {entries != null && entries.length > 0 && filtered.length === 0 && (
        <Card><Card.Body>
          <EmptyState
            icon={<AlertCircle />}
            title="No forms match this filter"
            description={`Nothing matches "${filter}" — try a shorter or different search term.`}
          />
        </Card.Body></Card>
      )}

      {filtered.length > 0 && (
        <Card padding="none">
          <table className="w-full text-[12.5px] text-v2-ink">
            <thead className="bg-v2-muted text-v2-ink-secondary uppercase text-[11px] tracking-wide">
              <tr>
                <th className="text-left px-4 py-2 font-semibold">Form</th>
                <th className="text-left px-4 py-2 font-semibold">Workflow</th>
                <th className="text-left px-4 py-2 font-semibold">Mode</th>
                <th className="text-left px-4 py-2 font-semibold">Schema</th>
                <th className="text-right px-4 py-2 font-semibold"> </th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((row) => (
                <tr key={`${row.workflowId}-${row.nodeId}`}
                    className="border-t border-v2-border-subtle hover:bg-v2-muted">
                  <td className="px-4 py-3">
                    <span className="font-medium text-v2-ink">{row.nodeLabel || row.nodeId}</span>
                    <div className="text-[11px] text-v2-ink-muted mt-0.5 font-mono">{row.nodeId}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-v2-ink">{row.workflowName || '(unnamed workflow)'}</span>
                    {row.workflowVersion != null && (
                      <Pill tone="muted" variant="soft">
                        <span className="ml-1">v{row.workflowVersion}</span>
                      </Pill>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <ModePill mode={row.assignmentMode} />
                  </td>
                  <td className="px-4 py-3 text-v2-ink-secondary">
                    <div>{row.fieldCount} field{row.fieldCount === 1 ? '' : 's'}</div>
                    {row.fieldTypes && (
                      <div className="text-[11px] text-v2-ink-muted mt-0.5">{row.fieldTypes}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Link
                      to={`/forms/design?wid=${encodeURIComponent(row.workflowId)}&nid=${encodeURIComponent(row.nodeId)}`}
                    >
                      <Button variant="primary" size="sm" iconLeft={<Pencil />}>
                        Open Designer
                      </Button>
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </V2PageShell>
  );
}

function ModePill({ mode }) {
  if (mode === 'native_assignee') return <Pill tone="success" variant="soft">native assignee</Pill>;
  if (mode === 'external_api')    return <Pill tone="cyan"    variant="soft">external API</Pill>;
  return <Pill tone="muted" variant="soft">approval center</Pill>;
}

/**
 * RC-FormDesigner-EmptyState (2026-06-23) — diagnostic empty state.
 *
 * <p>The catalog endpoints narrow by the active-scope chip
 * ({orgId, tenantId, instanceId}) per RC-210. When no forms match
 * the pin, the prior "No forms yet" + "Go to Workflows" was misleading
 * because forms might absolutely exist — just at a different scope.
 * This component surfaces the chip context directly so the operator
 * knows the exact filter that's hiding their data, and offers a
 * one-click "Show across full scope" escape that re-runs without the
 * chip narrowing (server still enforces the role's accessible
 * envelope — this only loosens the chip UX narrowing).</p>
 */
function EmptyAtChipScope({ orgId, tenantId, instanceId, surface, onShowUnfiltered }) {
  const hasAnyChip = orgId || tenantId || instanceId;
  return (
    <Card><Card.Body>
      <EmptyState
        icon={<FileText />}
        title={hasAnyChip ? 'No forms at this scope' : 'No forms yet'}
        description={
          hasAnyChip
            ? `Your active scope chip is narrowing the ${surface} view. Forms may exist in other ` +
              `tenants or instances within your role's reach. Try "Show across full scope" below, ` +
              `widen the chip in the top bar, or add a form_task to a workflow at this scope.`
            : `Add a form_task node to a workflow on the canvas, then return here to design its schema.`
        }
        primaryAction={
          hasAnyChip ? (
            <Button variant="primary" onClick={onShowUnfiltered}>
              Show across full scope
            </Button>
          ) : (
            <Link to="/canvas">
              <Button variant="primary">Go to Workflows</Button>
            </Link>
          )
        }
        secondaryAction={
          hasAnyChip ? (
            <Link to="/canvas">
              <Button variant="secondary">Go to Workflows</Button>
            </Link>
          ) : null
        }
      />
      {hasAnyChip && (
        <div className="mt-3 text-[11.5px] text-v2-ink-muted text-center">
          Active scope:
          {orgId      && <Pill tone="muted" variant="soft"><span className="font-mono">org {orgId.slice(0, 8)}…</span></Pill>}
          {tenantId   && <Pill tone="muted" variant="soft"><span className="font-mono">tenant {tenantId.slice(0, 8)}…</span></Pill>}
          {instanceId && <Pill tone="muted" variant="soft"><span className="font-mono">instance {instanceId.slice(0, 8)}…</span></Pill>}
        </div>
      )}
    </Card.Body></Card>
  );
}
